import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useBilling } from '../contexts/BillingContext';
import { AlertTriangle, XCircle, CreditCard, Lock, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import apiClient from '../utils/apiClient';
import { isIOSNative } from '../utils/nativeBridge';
import { toast } from 'sonner';

const BILLING_EXEMPT_PATHS = [
  '/billing', '/pricing', '/settings', '/onboarding', '/dashboard', '/learn',
];

export class BillingGateErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error, info) {
    // Silently recover from DOM manipulation errors (e.g. cookie consent scripts)
    console.warn('BillingAccessGate error boundary caught:', error?.message);
  }
  render() {
    if (this.state.hasError) {
      // Reset error state on next render cycle and pass through children
      setTimeout(() => this.setState({ hasError: false }), 0);
      return this.props.children;
    }
    return this.props.children;
  }
}

export function BillingAccessGate({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { billingStatus, isLocked, isRestricted, loading } = useBilling() || {};

  const isExempt = BILLING_EXEMPT_PATHS.some(p =>
    location.pathname === p || location.pathname.startsWith(p + '/')
  );

  // Don't gate while loading or if no billing status yet
  if (loading || !billingStatus?.has_subscription) return children;

  // Hard lock: only billing/pricing/settings accessible
  if (isLocked && !isExempt) {
    if (isIOSNative()) {
      // iOS App Store build: enforce the lock but never mention payments/fees.
      return (
        <div className="min-h-[60vh] flex items-center justify-center px-4" data-testid="hard-lock-gate">
          <div className="max-w-md text-center">
            <div className="mx-auto w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mb-5">
              <Lock className="h-8 w-8 text-slate-500" />
            </div>
            <h2 className="text-xl font-bold text-slate-800 mb-2">Workspace Locked</h2>
            <p className="text-sm text-muted-foreground mb-6">
              Locked. Check your email for details.
            </p>
            <Button variant="outline" onClick={() => navigate('/dashboard')} data-testid="go-to-dashboard-btn">
              Dashboard
            </Button>
          </div>
        </div>
      );
    }
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4" data-testid="hard-lock-gate">
        <div className="max-w-md text-center">
          <div className="mx-auto w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mb-5">
            <Lock className="h-8 w-8 text-red-600" />
          </div>
          <h2 className="text-xl font-bold text-red-700 mb-2">Account Locked</h2>
          <p className="text-sm text-muted-foreground mb-6">
            Locked for unpaid billing. Reactivate to restore access.
          </p>
          <div className="flex gap-3 justify-center">
            <Button
              onClick={() => navigate('/billing')}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="go-to-billing-btn"
            >
              <CreditCard className="h-4 w-4 mr-2" /> Go to Billing
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/dashboard')}
              data-testid="go-to-dashboard-btn"
            >
              Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Restricted view-only: show persistent banner + allow read access
  if (isRestricted) {
    if (isIOSNative()) {
      // iOS: enforce read-only, neutral copy, no payment CTA.
      return (
        <>
          <div className="sticky top-0 z-50 bg-slate-700 text-white px-4 py-2.5 text-center text-sm" data-testid="restricted-banner-global">
            <div className="flex items-center justify-center gap-2 flex-wrap">
              <AlertTriangle className="h-4 w-4 flex-shrink-0" />
              <span className="font-medium">Read-Only Mode</span>
              <span className="opacity-80">Some features are temporarily unavailable. Check your email for details.</span>
            </div>
          </div>
          {children}
        </>
      );
    }
    return (
      <>
        <div className="sticky top-0 z-50 bg-orange-600 text-white px-4 py-2.5 text-center text-sm" data-testid="restricted-banner-global">
          <div className="flex items-center justify-center gap-2 flex-wrap">
            <AlertTriangle className="h-4 w-4 flex-shrink-0" />
            <span className="font-medium">Read-Only Mode</span>
            <span className="opacity-80">AI, bank feeds, and write operations are disabled until payment is resolved.</span>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => navigate('/billing')}
              className="ml-2 h-7 text-xs bg-white text-orange-700 hover:bg-orange-50"
              data-testid="resolve-payment-btn"
            >
              Resolve Payment
            </Button>
          </div>
        </div>
        {children}
      </>
    );
  }

  // Card-required banner is rendered by GlobalBillingBanner inside
  // DashboardLayout (below the fixed mobile header), same as the
  // payment-failed strip. Nothing sticky here anymore.

  return children;
}

// Compact payment-failed strip. Rendered INSIDE DashboardLayout's <main>
// so it sits below the fixed mobile header and beside the desktop sidebar
// on every page (iter1314). Fires for past_due and never-paid subs.
export function GlobalBillingBanner() {
  const navigate = useNavigate();
  const location = useLocation();
  const { billingStatus, isPastDue, neverPaid, refreshBilling } = useBilling() || {};
  const [payingNow, setPayingNow] = useState(false);

  const onBillingPage = location.pathname === '/billing' || location.pathname.startsWith('/billing/');
  const onOnboarding = location.pathname.startsWith('/onboarding');
  // iOS App Store build: payment banners are never shown - users get the
  // full details by email instead.
  if (isIOSNative()) return null;
  if (onBillingPage || onOnboarding) return null;

  // Card-on-file is compulsory (Jul 2026 policy): compact strip for any
  // subscribed account without a card, when no payment-failed strip is
  // showing (that one already carries an Add Card CTA - never both).
  if (!(isPastDue || neverPaid)) {
    if (!billingStatus?.card_required) return null;
    return (
      <div className="bg-amber-50 border-b border-amber-200 text-amber-900 px-3 py-1.5" data-testid="card-required-banner-global">
        <div className="max-w-6xl mx-auto flex items-center gap-2">
          <CreditCard className="h-3.5 w-3.5 text-amber-600 flex-shrink-0" />
          <span className="text-xs sm:text-sm font-semibold whitespace-nowrap">Payment card required</span>
          <span className="hidden md:inline text-xs text-amber-800/80 truncate">
            {billingStatus?.status === 'trialing'
              ? "A card is required to keep your free trial: you won't be charged until the trial ends."
              : 'A card on file is required for your subscription. Please add one to avoid interruption.'}
          </span>
          <span className="flex-1" />
          <Button
            size="sm"
            variant="outline"
            onClick={() => navigate('/billing')}
            className="h-6 px-2.5 text-[11px] border-amber-400 text-amber-800 hover:bg-amber-100 bg-transparent flex-shrink-0"
            data-testid="add-card-banner-btn"
          >
            Add Card
          </Button>
        </div>
      </div>
    );
  }

  const hasCard = !!billingStatus?.has_payment_method;

  const handleInlinePayNow = async () => {
    setPayingNow(true);
    try {
      const { data } = await apiClient.post('/billing/pay-now');
      if (data.success) {
        toast.success(data.message || 'Payment successful. Access restored.');
        refreshBilling?.();
      } else {
        toast.error(`Payment declined: ${data.decline_reason || 'Please update your card.'}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Payment failed. Please try again.');
    } finally {
      setPayingNow(false);
    }
  };

  return (
    <div className="bg-amber-50 border-b border-amber-200 text-amber-900 px-3 py-1.5" data-testid="past-due-banner-global">
      <div className="max-w-6xl mx-auto flex items-center gap-2">
        <AlertTriangle className="h-3.5 w-3.5 text-amber-600 flex-shrink-0" />
        <span className="text-xs sm:text-sm font-semibold whitespace-nowrap">
          {neverPaid ? 'First payment failed' : 'Payment failed'}
        </span>
        <span className="hidden md:inline text-xs text-amber-800/80 truncate">
          {neverPaid
            ? 'This business is locked until its first payment completes.'
            : 'Your last charge was declined - fix it to keep everything running.'}
        </span>
        <span className="flex-1" />
        {hasCard && (
          <Button
            size="sm"
            onClick={handleInlinePayNow}
            disabled={payingNow}
            className="h-6 px-2.5 text-[11px] bg-amber-600 text-white hover:bg-amber-700 flex-shrink-0"
            data-testid="past-due-charge-now-btn"
          >
            {payingNow ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Charge Now'}
          </Button>
        )}
        <Button
          size="sm"
          variant="outline"
          onClick={() => navigate('/billing')}
          className="h-6 px-2.5 text-[11px] border-amber-400 text-amber-800 hover:bg-amber-100 bg-transparent flex-shrink-0"
          data-testid="past-due-update-card-btn"
        >
          {hasCard ? 'Update Card' : 'Add Card'}
        </Button>
      </div>
    </div>
  );
}
